import { getCategoryById } from "../videoStudio/categories";
import { defaultFormBase } from "../videoStudio/components/VideoForm";

const STORAGE_KEY = "exploreIdeasVideoStudioPending";

/**
 * @param {string} basePrompt
 * @param {string} productDescription
 * @param {string} dialog
 */
export function buildRecreatePrompt(basePrompt, productDescription, dialog) {
  const parts = [
    "Replace the on-camera person in the reference video with the uploaded character while preserving motion, timing, and camera work.",
    String(basePrompt || "").trim(),
  ];
  const desc = String(productDescription || "").trim();
  const lines = String(dialog || "").trim();
  if (desc) parts.push(`Product: ${desc}`);
  parts.push("The character naturally holds, displays, or interacts with the product throughout the ad.");
  if (lines) parts.push(`Dialog: ${lines}`);
  return parts.filter(Boolean).join("\n\n");
}

function normalizeAspect(aspect) {
  const raw = String(aspect || "9/16").trim();
  if (raw.includes(":")) return raw;
  if (raw.includes("/")) return raw.replace("/", ":");
  return "9:16";
}

/**
 * @param {File} file
 * @returns {Promise<{ name: string, type: string, dataUrl: string }>}
 */
function fileToStored(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve({
        name: file.name,
        type: file.type,
        dataUrl: reader.result,
      });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * @param {{ name?: string, type?: string, dataUrl: string }} stored
 * @returns {File}
 */
function storedToFile(stored) {
  const raw = String(stored.dataUrl || "");
  const [header, b64] = raw.split(",");
  const mime = stored.type || header.match(/:(.*?);/)?.[1] || "application/octet-stream";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new File([arr], stored.name || "upload.jpg", { type: mime });
}

/**
 * @param {string} url
 * @param {string} [name]
 * @returns {Promise<File>}
 */
async function fetchUrlToFile(url, name = "reference.mp4") {
  const res = await fetch(url, { redirect: "follow" });
  if (!res.ok) throw new Error(`Could not fetch reference video (${res.status}).`);
  const blob = await res.blob();
  return new File([blob], name, { type: blob.type || "video/mp4" });
}

/**
 * @param {File} file
 */
function uploadEntryFromFile(file) {
  return {
    id: `explore-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    file,
    previewUrl: URL.createObjectURL(file),
    name: file.name,
  };
}

/**
 * @param {object} template
 * @param {{ characterFile: File, productFile: File, productDescription?: string, dialog?: string }} formData
 */
export async function openExploreRecreateInVideoStudio(template, formData) {
  const categoryId = "video-to-video";
  const category = getCategoryById(categoryId);
  const basePrompt = template.recreate?.prompt || template.description || "";
  const prompt = buildRecreatePrompt(
    basePrompt,
    formData.productDescription,
    formData.dialog,
  );

  const payload = {
    categoryId,
    modelId: category.defaultModelId,
    title: String(template.title || template.recreate?.title || "Explore Ideas").trim(),
    prompt,
    aspectRatio: normalizeAspect(template.aspect),
    referenceVideoUrl: String(template.video || "").trim(),
    templateId: template.id,
    characterImage: await fileToStored(formData.characterFile),
    productImage: await fileToStored(formData.productFile),
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    throw new Error("Uploads are too large. Try smaller JPG or PNG files (under 5 MB each).");
  }

  window.location.assign("/user?page=video-studio");
}

/** @returns {object | null} */
export function readExploreIdeasPending() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearExploreIdeasPending() {
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Build a Video Studio form from Explore Ideas pending payload.
 * @param {object} pending
 * @param {string} categoryId
 */
export async function hydrateFormFromExploreIdeas(pending, categoryId) {
  const category = getCategoryById(categoryId);
  const base = defaultFormBase(category);

  /** @type {ReturnType<typeof defaultFormBase>} */
  const form = {
    ...base,
    model: pending.modelId || category.defaultModelId,
    title: String(pending.title || "").trim(),
    prompt: String(pending.prompt || "").trim(),
    aspectRatio: pending.aspectRatio || "9:16",
    imageUrlUploads: [],
    referenceImageUploads: [],
    referenceVideoUploads: [],
  };

  if (pending.characterImage?.dataUrl) {
    form.imageUrlUploads = [uploadEntryFromFile(storedToFile(pending.characterImage))];
  }
  if (pending.productImage?.dataUrl) {
    form.referenceImageUploads = [uploadEntryFromFile(storedToFile(pending.productImage))];
  }

  const videoUrl = String(pending.referenceVideoUrl || "").trim();
  if (videoUrl) {
    try {
      const file = await fetchUrlToFile(videoUrl, `template-${pending.templateId || "ref"}.mp4`);
      form.referenceVideoUploads = [uploadEntryFromFile(file)];
    } catch {
      form.referenceVideoUploads = [
        {
          id: `remote-video-${Date.now()}`,
          file: null,
          previewUrl: videoUrl,
          name: "reference.mp4",
        },
      ];
    }
  }

  return form;
}
